import React from 'react'
import styled from 'styled-components'
import { SupportedChainId } from '../../constants/networks'
import { QueueExecutionsFilters } from '../../hooks/useQueueExecutions'
import { QueueExecutionStatus } from '../../types/API'
import { capitalizeFirstLetter } from '../../utils/math-utils'

interface Props {
  filters: QueueExecutionsFilters
  setFilters: (filters: QueueExecutionsFilters) => void
}

const SUPPORTED_CHAINS = Object.entries(SupportedChainId).map(([key, value]) => ({
  label: capitalizeFirstLetter(key.toLowerCase()) as string,
  value,
}))

function QueueFilters({ filters, setFilters }: Props) {
  const handleMimicIdFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setFilters({ mimicId: value ? value : undefined })
  }

  const handleChainIdFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target
    setFilters({ chainId: value ? parseInt(value) : undefined })
  }

  const handleStatusFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target
    setFilters({ status: value === 'all' ? undefined : value })
  }

  const handleOrderFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target
    setFilters({ order: value as 'asc' | 'desc' })
  }

  return (
    <FiltersBar>
      <FilterGroup>
        <FilterLabel>Mimic ID</FilterLabel>
        <FilterInput type="text" placeholder="Filter..." value={filters.mimicId ?? ''} onChange={handleMimicIdFilterChange} />
      </FilterGroup>
      <FilterGroup>
        <FilterLabel>Chain</FilterLabel>
        <FilterSelect value={filters.chainId ?? ''} onChange={handleChainIdFilterChange}>
          <option value="">All</option>
          {SUPPORTED_CHAINS.map((chain) => (
            <option key={chain.value} value={chain.value}>{chain.label}</option>
          ))}
        </FilterSelect>
      </FilterGroup>
      <FilterGroup>
        <FilterLabel>Status</FilterLabel>
        <FilterSelect value={filters.status ?? 'all'} onChange={handleStatusFilterChange}>
          <option value="all">All</option>
          {Object.values(QueueExecutionStatus).map((status) => (
            <option key={status} value={status}>{capitalizeFirstLetter(status)}</option>
          ))}
        </FilterSelect>
      </FilterGroup>
      <FilterGroup>
        <FilterLabel>Order</FilterLabel>
        <FilterSelect value={filters.order ?? 'asc'} onChange={handleOrderFilterChange}>
          <option value="asc">Asc</option>
          <option value="desc">Desc</option>
        </FilterSelect>
      </FilterGroup>
    </FiltersBar>
  )
}

export default QueueFilters

// ─── Styles ──────────────────────────────────────────────────────────────────

const FiltersBar = styled.div`display: flex; flex-wrap: wrap; gap: 10px; align-items: flex-end;`
const FilterGroup = styled.div`display: flex; flex-direction: column; gap: 4px;`
const FilterLabel = styled.label`font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: #a1a1a1;`
const FilterInput = styled.input`
  height: 28px; padding: 0 8px; background: #111; border: 1px solid rgba(255,255,255,0.07); border-radius: 5px;
  color: #a0a0a0; font-size: 12px; outline: none; width: 160px;
  &:focus { border-color: rgba(111,92,230,0.4); }
`
const FilterSelect = styled.select`
  height: 28px; padding: 0 8px; background: #111; border: 1px solid rgba(255,255,255,0.07); border-radius: 5px;
  color: #a0a0a0; font-size: 12px; outline: none; cursor: pointer;
  &:focus { border-color: rgba(111,92,230,0.4); }
`
